import { useMemo, useState } from "react";

import { useChatJobsByMessages, useChatMessages } from "~/entities/chat/useActiveChat";
import { useGenerationSettings } from "~/entities/model";
import { useGenerationStream } from "~/features/generation-stream/useGenerationStream";
import { useJobsPolling } from "~/features/generation-stream/useJobsPolling";
import { buildRepeatPrefill } from "~/features/repeat-generation";
import { useSubmitLocalMessage } from "~/features/send-local-message/useSubmitLocalMessage";
import { useStartGeneration } from "~/features/start-generation/useStartGeneration";

import type { AttachmentCreate, ChatMessage } from "~/entities/chat";

// Поток сообщений + генерации активного чата: messages/jobs, SSE-стрим с
// fallback-поллингом, submit нового сообщения и retry. `scrollToMessageRef`
// здесь не нужен — он чисто render-time wiring (VirtualMessageList ↔
// ChatHeader in-chat search), его разводит сам ChatShell.
export function useChatGenerationFlow(chatId: string | undefined) {
    const messagesQuery = useChatMessages(chatId);
    const submitLocal = useSubmitLocalMessage();
    const startGeneration = useStartGeneration();
    // Справочник моделей нужен разбору снимка: без него режим повтора не
    // резолвится и заказ не собрать (тогда повтор идёт прежним путём).
    const modelsQuery = useGenerationSettings();
    const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null);

    const handleSubmit = (prompt: string, attachments: AttachmentCreate[]) => {
        if (!chatId) return;
        void (async () => {
            const message = await submitLocal(chatId, prompt, attachments);
            if (message) {
                await startGeneration({
                    chatUuid: chatId,
                    messageId: message.id,
                    inputAttachments: message.attachments ?? [],
                });
            }
        })();
    };

    const messages = useMemo(() => messagesQuery.data?.messages ?? [], [messagesQuery.data?.messages]);
    const hasMessages = messages.length > 0;
    const messageIds = useMemo(() => messages.map((m) => m.id), [messages]);
    const { jobs } = useChatJobsByMessages(chatId, messageIds);

    // «Повторить» отправляет ровно тот заказ, который повторяют: настройки и
    // раскладку входов берём из снимка его генерации, а не из промпт-бара. Бар к
    // этому моменту показывает уже другое (человек сменил модель или перезагрузил
    // страницу), и повтор молча уходил бы дешевле и другим — 2K ×4 превращались в
    // 1024p ×1. Снимка нет или он не разбирается (модель убрали с платформы) —
    // остаётся прежнее поведение, сборка из бара.
    const handleRetry = async (message: ChatMessage) => {
        if (!chatId) return;
        const job = jobs[message.id];
        const order = job ? buildRepeatPrefill({ message, job, models: modelsQuery.data }) : null;
        setRetryingMessageId(message.id);
        try {
            await startGeneration({
                chatUuid: chatId,
                messageId: message.id,
                inputAttachments: message.attachments ?? [],
                ...(order ? { repeat: order } : {}),
            });
        } finally {
            setRetryingMessageId(null);
        }
    };

    const hasInFlightJobs = useMemo(
        () =>
            Object.values(jobs).some(
                (j) => j.status === "queued" || j.status === "in_progress" || j.status === "recovering",
            ),
        [jobs],
    );

    const { isConnected: sseConnected } = useGenerationStream(chatId ?? null);
    // Polling — только страховка: включаем лишь когда (а) есть активная
    // джоба и (б) SSE реально мёртв. Когда стрим работает — ноль
    // фоновых запросов. См. useJobsPolling.
    useJobsPolling(chatId, hasInFlightJobs && !sseConnected);

    return {
        messages,
        hasMessages,
        isMessagesLoading: messagesQuery.isLoading,
        // Лента держит хвост чата; более ранние сообщения догружаются, когда
        // пользователь домотал до верха (см. VirtualMessageList).
        hasOlderMessages: messagesQuery.hasNextPage,
        isLoadingOlderMessages: messagesQuery.isFetchingNextPage,
        loadOlderMessages: messagesQuery.fetchNextPage,
        jobs,
        retryingMessageId,
        handleSubmit,
        handleRetry,
    };
}
